import { useQuery } from '@apollo/client';
import { Box, VStack } from '@chakra-ui/react';

import { CompanyLegalBeneficiaryOnboardingStatus } from '__generated__/GQL';
import asBentoPage from 'common/bento/hoc/asBentoPage';
import useCompanyContext from 'common/hooks/useCompanyContext';
import Callout from 'components/_core/Callout';
import SunshineCard from 'components/_core/SunshineCard';
import SquircleIcon from 'components/_core/SunshineSquircleIcon';
import Text from 'components/_core/Text';
import SuspenseQuery from 'components/SuspenseQuery';
import Header from 'features/Onboarding/components/Header';
import OBNavigation from 'features/Onboarding/components/OBNavigation/OBNavigation';

import { ApplicationSelectionMandateeDocument } from './graphql/queries/ApplicationSelectionMandatee.gql';
import { getApplications, MandateeApplication } from './helpers';
import locales from './locales';

const ONBOARDING_STATUS_REQUIRING_ACTION = [
  CompanyLegalBeneficiaryOnboardingStatus.NotStarted,
  CompanyLegalBeneficiaryOnboardingStatus.InProgress,
  CompanyLegalBeneficiaryOnboardingStatus.Invalid,
];

interface ApplicationSelectionMandateeProps {
  onSelectStartNewApplication: () => void;
  onSelectPendingApplication: () => void;
}

const getPendingApplicationData = ({
  company,
  companyUser,
}: MandateeApplication) => {
  const requiresMandateeAction = !companyUser.isAwaitingAccountValidation;

  if (requiresMandateeAction) {
    return {
      icon: <SquircleIcon colorScheme="orange" name="warning" />,
      subtitle: locales.pendingApplications.requiresMandateeAction,
    };
  }

  const requiresBeneficiariesAction = company.legalBeneficiaries.some(
    ({ onboardingStatus }) =>
      ONBOARDING_STATUS_REQUIRING_ACTION.includes(onboardingStatus),
  );

  if (requiresBeneficiariesAction) {
    return {
      icon: <SquircleIcon colorScheme="orange" name="warning" />,
      subtitle: locales.pendingApplications.requiresBeneficiariesAction,
    };
  }

  return {
    icon: <SquircleIcon colorScheme="orange" name="timer" />,
    subtitle: locales.pendingApplications.pending,
  };
};

const ApplicationSelectionMandatee =
  asBentoPage<ApplicationSelectionMandateeProps>(
    ({ Layout, onSelectPendingApplication, onSelectStartNewApplication }) => {
      const query = useQuery(ApplicationSelectionMandateeDocument);
      const { setCompanyContext } = useCompanyContext();

      return (
        <Layout nav={<OBNavigation />}>
          <SuspenseQuery query={query}>
            {(queryResult) => {
              const { isNewOnboardingAllowed, profile } = queryResult.viewer;

              const {
                pendingApplications,
                refusedApplications,
                validatedApplications,
              } = getApplications(queryResult);

              return (
                <Box paddingBottom="space-48">
                  <Header
                    description={locales.description}
                    title={locales.formatString(locales.title, {
                      firstName: profile.firstName,
                    })}
                  />

                  <VStack alignItems="stretch" spacing="space-24">
                    {pendingApplications.length > 0 ? (
                      <VStack alignItems="strech" spacing="space-8">
                        <Text size="sm" variant="light">
                          {locales.formatString(
                            locales.pendingApplications.sectionHeader,
                            {
                              numberOfApplications: pendingApplications.length,
                            },
                          )}
                        </Text>

                        <SunshineCard.Group>
                          {pendingApplications.map((application) => {
                            const { icon, subtitle } =
                              getPendingApplicationData(application);
                            const { company, companyUser } = application;

                            return (
                              <SunshineCard
                                key={company.companyProfileId}
                                onClick={() => {
                                  // Calling setCompanyContext not in an xstate action and using setTimeout
                                  // because otherwise the companyContext is not updated when the guards are called
                                  setCompanyContext({
                                    companyProfileId: company.companyProfileId,
                                    companyUserId: companyUser.companyUserId,
                                  });
                                  setTimeout(() => {
                                    onSelectPendingApplication();
                                  }, 0);
                                }}
                                title={
                                  company.profile.legalName ||
                                  locales.pendingApplications.defaultCompanyName
                                }
                              >
                                <SunshineCard.Slot name="aside">
                                  {icon}
                                </SunshineCard.Slot>

                                <SunshineCard.Content>
                                  <SunshineCard.Text>
                                    {subtitle}
                                  </SunshineCard.Text>
                                </SunshineCard.Content>

                                <SunshineCard.Slot name="action">
                                  <SunshineCard.SunshineIcon name="chevron-right" />
                                </SunshineCard.Slot>
                              </SunshineCard>
                            );
                          })}
                        </SunshineCard.Group>
                      </VStack>
                    ) : null}

                    <VStack alignItems="strech" spacing="space-8">
                      {isNewOnboardingAllowed ? (
                        <>
                          <Text size="sm" variant="light">
                            {locales.newApplication.sectionHeader}
                          </Text>

                          <SunshineCard
                            onClick={onSelectStartNewApplication}
                            title={locales.newApplication.title}
                          >
                            <SunshineCard.Slot name="aside">
                              <SquircleIcon name="company" />
                            </SunshineCard.Slot>

                            <SunshineCard.Content>
                              <SunshineCard.Text>
                                {locales.newApplication.subtitle}
                              </SunshineCard.Text>
                            </SunshineCard.Content>

                            <SunshineCard.Slot name="action">
                              <SunshineCard.SunshineIcon name="chevron-right" />
                            </SunshineCard.Slot>
                          </SunshineCard>
                        </>
                      ) : (
                        <Callout>
                          <Callout.Content>
                            {locales.multiOnboardingInfo}
                          </Callout.Content>
                        </Callout>
                      )}
                    </VStack>

                    {validatedApplications.length > 0 ? (
                      <VStack alignItems="strech" spacing="space-8">
                        <Text size="sm" variant="light">
                          {locales.formatString(
                            locales.validatedApplications.sectionHeader,
                            {
                              numberOfApplications:
                                validatedApplications.length,
                            },
                          )}
                        </Text>

                        <SunshineCard.Group>
                          {validatedApplications.map(({ company }) => (
                            <SunshineCard
                              isDisabled
                              key={company.companyProfileId}
                              title={company.profile.legalName}
                            >
                              <SunshineCard.Slot name="aside">
                                <SquircleIcon colorScheme="green" name="tick" />
                              </SunshineCard.Slot>

                              <SunshineCard.Content>
                                <SunshineCard.Text>
                                  {locales.validatedApplications.subtitle}
                                </SunshineCard.Text>
                              </SunshineCard.Content>
                            </SunshineCard>
                          ))}
                        </SunshineCard.Group>
                      </VStack>
                    ) : null}

                    {refusedApplications.length > 0 ? (
                      <VStack alignItems="strech" spacing="space-8">
                        <Text size="sm" variant="light">
                          {locales.formatString(
                            locales.refusedApplications.sectionHeader,
                            {
                              numberOfApplications: refusedApplications.length,
                            },
                          )}
                        </Text>

                        <SunshineCard.Group>
                          {refusedApplications.map(({ company }) => (
                            <SunshineCard
                              isDisabled
                              key={company.companyProfileId}
                              title={company.profile.legalName}
                            >
                              <SunshineCard.Slot name="aside">
                                <SquircleIcon colorScheme="red" name="cross" />
                              </SunshineCard.Slot>

                              <SunshineCard.Content>
                                <SunshineCard.Text>
                                  {locales.refusedApplications.subtitle}
                                </SunshineCard.Text>
                              </SunshineCard.Content>
                            </SunshineCard>
                          ))}
                        </SunshineCard.Group>
                      </VStack>
                    ) : null}
                  </VStack>
                </Box>
              );
            }}
          </SuspenseQuery>
        </Layout>
      );
    },
  );

export default ApplicationSelectionMandatee;
